/**
 * Region-specific brand colors.
 *
 * The backend stores one palette per region (settings keys `theme.light_colors`
 * and `theme.dark_colors`, region-overridable exactly like `site.logo`) and
 * ships both on every `/api/settings` response. Here they are turned into a
 * stylesheet that redefines the matching custom properties from
 * `app/globals.css`, so every `bg-primary` / `text-foreground` utility in the
 * app follows the region without a single component knowing about regions.
 */

/**
 * Setting token name -> CSS custom property. Must stay in step with
 * `config/settings.php`'s `theme.*_colors.tokens` on the backend; a token the
 * backend adds but this map omits is simply ignored, never emitted raw.
 */
export const THEME_TOKENS = {
  background: "--background",
  foreground: "--foreground",
  primary: "--primary",
  secondary: "--secondary",
  surface: "--surface",
  surface_elevated: "--surface-elevated",
  sidebar: "--sidebar",
  topbar: "--topbar",
  card: "--card",
  modal: "--modal",
  input: "--input",
  table: "--table",
  hover: "--hover",
  selected: "--selected",
  border: "--border",
  border_subtle: "--border-subtle",
  text_secondary: "--text-secondary",
  text_muted: "--text-muted",
  success: "--success",
  warning: "--warning",
  danger: "--danger",
  status_info: "--status-info",
  neutral: "--neutral",
  accent: "--accent",
  excuse_teacher: "--excuse-teacher",
  excuse_student: "--excuse-student",
  focus_ring: "--focus-ring",
  backdrop: "--backdrop",
  skeleton: "--skeleton",
  active: "--active",
  on_active: "--on-active",
  on_primary: "--on-primary",
  on_secondary: "--on-secondary",
  on_success: "--on-success",
  on_warning: "--on-warning",
  on_danger: "--on-danger",
  on_status_info: "--on-status-info",
  primary_hover: "--primary-hover",  // follows --primary unless set
  secondary_hover: "--secondary-hover",  // follows --secondary unless set
  accent_hover: "--accent-hover",  // follows --accent unless set
  danger_hover: "--danger-hover",  // follows --danger unless set
  success_hover: "--success-hover",  // follows --success unless set
  warning_hover: "--warning-hover",  // follows --warning unless set
  banner_from: "--banner-from",
  banner_to: "--banner-to",
  footer: "--footer",  // follows --primary unless set
  on_footer: "--on-footer",
  info: "--info",
  gray: "--gray",
  nav_button: "--nav-button",  // follows --primary unless set
  on_nav_button: "--on-nav-button",  // follows --on-primary unless set
  nav_button_hover: "--nav-button-hover",  // follows --secondary unless set
  nav_text: "--nav-text",  // follows --primary unless set
  nav_hover: "--nav-hover",  // follows --hover unless set
  course_cover: "--course-cover",  // follows --primary unless set
  on_course_cover: "--on-course-cover",  // follows --on-primary unless set
  course_cover_soft: "--course-cover-soft",  // follows --secondary unless set
  on_course_cover_soft: "--on-course-cover-soft",  // follows --secondary unless set
} as const;

export type ThemeTokenName = keyof typeof THEME_TOKENS;

export type ThemePalette = Partial<Record<ThemeTokenName, string>>;

export type SiteTheme = {
  light: ThemePalette;
  dark: ThemePalette;
};

export const THEME_STYLE_ELEMENT_ID = "region-theme";

// 6- or 8-digit only: the backend expands every shorthand before it stores a
// value, so anything shorter arriving here did not come from the settings form.
// The alpha form carries the translucent tokens (--backdrop, dark --hover).
const HEX_COLOR = /^#(?:[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

/**
 * Keep only known tokens holding a canonical `#RRGGBB` value. The result is
 * interpolated into a `<style>` element, so anything else — a named color, a
 * `url(...)`, a stray `;` — is dropped rather than sanitized: the backend
 * already normalizes on write, so a value failing here is not a value a
 * legitimate admin typed.
 */
export function normalizeThemePalette(value: unknown): ThemePalette {
  if (typeof value !== "object" || value === null) {
    return {};
  }

  const source = value as Record<string, unknown>;
  const palette: ThemePalette = {};

  for (const token of Object.keys(THEME_TOKENS) as ThemeTokenName[]) {
    const color = source[token];

    if (typeof color === "string" && HEX_COLOR.test(color.trim())) {
      palette[token] = color.trim().toUpperCase();
    }
  }

  return palette;
}

export function normalizeSiteTheme(value: unknown): SiteTheme {
  const source = typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

  return {
    light: normalizeThemePalette(source.light),
    dark: normalizeThemePalette(source.dark),
  };
}

function declarations(palette: ThemePalette): string {
  return (Object.keys(THEME_TOKENS) as ThemeTokenName[])
    .filter((token) => palette[token])
    .map((token) => `${THEME_TOKENS[token]}:${palette[token]}`)
    .join(";");
}

/**
 * Build the stylesheet that overrides `app/globals.css`.
 *
 * The selectors are deliberately doubled (`:root:root`): globals.css defines
 * these tokens on plain `:root` and `.dark`, and the bundled stylesheet's
 * position relative to this inline tag is not guaranteed, so winning on
 * specificity rather than source order is what makes the override reliable.
 * `.dark` is matched as a descendant because ThemeContext puts the class on
 * `<body>` and on a wrapper `<div>`, never on `<html>`.
 */
export function buildThemeCss(theme: SiteTheme | null | undefined): string {
  if (!theme) {
    return "";
  }

  const rules: string[] = [];
  const light = declarations(theme.light);
  const dark = declarations(theme.dark);

  if (light) {
    rules.push(`:root:root{${light}}`);
  }

  if (dark) {
    rules.push(`:root:root .dark,:root:root.dark{${dark}}`);
  }

  return rules.join("");
}
